import { resolveListState } from "../lib/listViewState";

export type CmmdProjectListEmptyReason = "no-match" | "no-projects";

/**
 * Like `ListViewState`, but the rows case carries whether a refresh is still in
 * flight behind them. The list stays interactive either way; the flag only lets
 * the view say so.
 */
export type CmmdProjectListState =
  | { readonly kind: "loading" }
  | { readonly kind: "error"; readonly message: string }
  | { readonly kind: "empty"; readonly reason: CmmdProjectListEmptyReason }
  | { readonly kind: "rows"; readonly isRefreshing: boolean };

export interface CmmdProjectListStateInput {
  readonly isLoading: boolean;
  readonly error: string | null;
  /** Row count AFTER the palette's search filter has been applied. */
  readonly rowCount: number;
  /** Unfiltered project count, which is what makes "no match" distinguishable. */
  readonly sourceCount: number;
}

/**
 * Rendering state for the Add project -> CMMD Project list.
 *
 * Loading and error only win when there is nothing to show. Opening that view
 * fires an authoritative refresh that takes 10-18s against forge-cloud, but the
 * cached snapshot has already been published to the store by then, so the rows
 * are there the whole time. Gating on `isLoading` alone hid them behind a
 * spinner for the length of the refresh, which reads as the picker being slow
 * to load when it is really refusing to draw what it already has.
 *
 * This is `resolveListState`'s existing rule, which the sibling context list
 * was already using with the same inputs.
 */
export function resolveCmmdProjectListState(
  input: CmmdProjectListStateInput,
): CmmdProjectListState {
  const state = resolveListState({
    isLoading: input.isLoading,
    error: input.error,
    rowCount: input.rowCount,
    emptyReason: input.sourceCount === 0 ? "no-projects" : "no-match",
  });
  return state.kind === "rows" ? { kind: "rows", isRefreshing: input.isLoading } : state;
}
